import React, { useState, useEffect } from "react";
import "./CreateTaskForm.scss";

interface Task {
  id: number;
  title: string;
  description: string;
  due_date?: string;
  reminder_date?: string;
  reminder_time?: string;
  reminder_frequency?: string;
  status: string;
  tags?: string[];
  notes?: string;
  created_at: string;
}

interface Props {
  onTaskCreated: (task: Task) => void;
  editingTask?: Task | null;
}

const CreateTaskForm: React.FC<Props> = ({ onTaskCreated, editingTask }) => {
  const [title, setTitle] = useState(editingTask?.title || "");
  const [description, setDescription] = useState(editingTask?.description || "");
  const [notes, setNotes] = useState(editingTask?.notes || "");
  const [dueDate, setDueDate] = useState(editingTask?.due_date || "");
  const [reminderDate, setReminderDate] = useState(editingTask?.reminder_date || "");
  const [reminderTime, setReminderTime] = useState(editingTask?.reminder_time || "");
  const [reminderFrequency, setReminderFrequency] = useState(editingTask?.reminder_frequency || "");
  const [status, setStatus] = useState(editingTask?.status || "pending");
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>(editingTask?.tags || []);
  const [newTag, setNewTag] = useState("");

  useEffect(() => {
    const storedTags = JSON.parse(localStorage.getItem("tags") || "[]");
    setAvailableTags(storedTags);
  }, []);

  const addTag = () => {
    if (newTag && !availableTags.includes(newTag)) {
      const updated = [...availableTags, newTag];
      localStorage.setItem("tags", JSON.stringify(updated));
      setAvailableTags(updated);
    }
    if (newTag && !selectedTags.includes(newTag)) {
      setSelectedTags([...selectedTags, newTag]);
    }
    setNewTag("");
  };

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return alert("Title required");

    const existing = JSON.parse(localStorage.getItem("tasks") || "[]");
    let updatedTask: Task;

    if (editingTask) {
      updatedTask = { ...editingTask, title, description, notes, due_date: dueDate, tags: selectedTags, status };
      const updated = existing.map((t: Task) => (t.id === editingTask.id ? updatedTask : t));
      localStorage.setItem("tasks", JSON.stringify(updated));
    } else {
      updatedTask = {
        id: Date.now(),
        title,
        description,
        notes,
        due_date: dueDate,
        reminder_date: reminderDate,
        reminder_time: reminderTime,
        reminder_frequency: reminderFrequency,
        status,
        tags: selectedTags,
        created_at: new Date().toISOString(),
      };
      localStorage.setItem("tasks", JSON.stringify([...existing, updatedTask]));
    }

    onTaskCreated(updatedTask);
  };

  return (
    <form className="create-task-form" onSubmit={handleSubmit}>
      <h2>{editingTask ? "Edit Task" : "Create Task"}</h2>

      <label>Title*</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} required />

      <label>Description</label>
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} />

      <label>Notes</label>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />

      <label>Due Date</label>
      <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />

      <label>Reminder </label>
      <input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} />

    
      <input type="time" value={reminderTime} onChange={(e) => setReminderTime(e.target.value)} />

      
      <select value={reminderFrequency} onChange={(e) => setReminderFrequency(e.target.value)}>
        <option value="">Select frequency</option>
        <option value="daily">Daily</option>
        <option value="weekly">Weekly</option>
        <option value="monthly">Monthly</option>
      </select>

      <label>Tags</label>
      <div className="tags-section">
        <div className="tag-options">
          {availableTags.map((tag) => (
            <span
              key={tag}
              className={`tag ${selectedTags.includes(tag) ? "selected" : ""}`}
              onClick={() => toggleTag(tag)}
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="tag-input">
          <input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="Add new tag and press Enter"
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
          />
        </div>
      </div>

      <button type="submit" className="submit-btn">{editingTask ? "Save Changes" : "Create Task"}</button>
    </form>
  );
};

export default CreateTaskForm;
